import { getApiErrorCode, translateApiError } from "@/lib/api/api.client";
import Button from "@/lib/components/ui/button/button";
import TriangleAlert from "@/lib/components/ui/icons/triangle-alert";
import Text from "@/lib/components/ui/text/text";
import { ORG_SELECTED_KEY } from "@/modules/tenants/components/org-chooser";
import { log } from "@soulbyte/core";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useRouteError } from "react-router";

// third-party vendor names sometimes leak into backend error text (e.g. a
// Frontegg webhook delay) — users should never learn which tools we run on
const INTERNAL_TOOL_NAMES = ["frontegg"];

// a company nobody has activated yet has no tenants row, so every tenant read
// 404s with this code (soulbyte-core, svc-tenant)
const TENANT_NOT_PROVISIONED = "TENANT_NOT_PROVISIONED";

function isTenantNotProvisioned(error: unknown): boolean {
  return getApiErrorCode(error) === TENANT_NOT_PROVISIONED;
}

function hideInternalToolNames(message: string): string {
  return INTERNAL_TOOL_NAMES.reduce(
    (text, name) => text.replace(new RegExp(`\\s*\\b${name}\\b`, "gi"), ""),
    message,
  )
    .replace(/\s{2,}/g, " ")
    .trim();
}

export default function AppErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();
  // translateApiError, not getErrorMessage: the backend writes its messages for
  // us, not for the reader — "tenant rows are created by the auth webhook" means
  // nothing to someone who just logged in. a known error code has a written
  // German and English sentence behind it; anything else still falls back
  const message = hideInternalToolNames(translateApiError(error));

  useEffect(() => {
    log.error(error, {
      tags: {
        error_type: "error_boundary",
        error_source: "route",
      },
      extra: { error },
    });
  }, [error]);

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <div className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-300 w-full max-w-md space-y-4 text-center">
        <div className="flex justify-center">
          <div className="bg-background-critical/10 text-foreground-critical motion-safe:animate-pulse flex size-12 items-center justify-center rounded-full">
            <TriangleAlert className="size-6" />
          </div>
        </div>
        <div className="space-y-2">
          <Text as="h1" variant="heading" size="md">
            {t("common_error")}
          </Text>
          <Text variant="body" size="sm" className="text-muted-foreground">
            {message}
          </Text>
          {isTenantNotProvisioned(error) && (
            <Text variant="body" size="sm" className="text-muted-foreground">
              <Trans
                t={t}
                i18nKey="error_boundary_tenant_support"
                components={{
                  mailLink: (
                    <a
                      href="mailto:dom@edge-digital.ai"
                      className="underline underline-offset-4"
                    />
                  ),
                }}
              />
            </Text>
          )}
        </div>
        <ErrorActions error={error} />
      </div>
    </div>
  );
}

/**
 * ErrorActions offers whatever actually gets past the error on screen. A
 * company nobody has activated yet throws from inside the app layout, so home
 * and reload both walk straight back into it and only dropping the company
 * picked for this session leads anywhere. Every other error is the ordinary
 * transient kind, where reload is the fix.
 */
function ErrorActions({ error }: { error: unknown }) {
  const { t } = useTranslation();

  if (isTenantNotProvisioned(error)) {
    return (
      <Button asChild>
        <a href="/" onClick={() => sessionStorage.removeItem(ORG_SELECTED_KEY)}>
          {t("error_boundary_choose_company")}
        </a>
      </Button>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2">
      <Button asChild variant="secondary" className="flex-1">
        <a href="/">{t("error_boundary_back_home")}</a>
      </Button>

      <Button onClick={() => window.location.reload()} className="flex-1">
        {t("error_boundary_reload_page")}
      </Button>
    </div>
  );
}
